import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import ButtonScogoPrimary from '../../../common/Buttons/ButtonScogoPrimary';
import Form from '../../../common/Form';
import { actualValueForDropdown, validateStringValue, findPincode } from '../../../utils/common';
import CreateTicketForm from './CreateTicketForm';
import { createServiceRequest } from '../../../actions/service_request';
import { openCustomModal } from '../../../actions/modal';
import AddNewPincode from '../Modal/AddNewPincode';
import { convertIntoPaisa } from '../../../utils/utils';
import { CLEAR_CREATED_ASSET_IN_STORE } from '../../../types/inventory';
import { SEND_TOAST } from '../../../types/toast';
import { MilestoneType } from '../TicketController';
import { transformAddress } from '../../../components/Inputs/Address';
import moment from 'moment';

const createTicketForm = 'createTicketForm';

const CreateTicket = ({ closeRaise, selectedTicketId = 0 }) => {
    const ticketList = useSelector((state) => state.tickets.ticketList);
    const selectedTicket = selectedTicketId && ticketList.find(ticket => ticket.ticket_id === selectedTicketId);
    const dispatch = useDispatch();
    const methods = useForm({ defaultValues: {}, mode: 'onChange' });
    const [projectMilestones, setProjectMilestones] = useState([]);
    const submitRef = useRef();
    const createdAsset = useSelector((state) => state.inventory.createdAsset);
    const [showBoq, setShowBoq] = useState(false);
    const lastSubmitDataRef = useRef(null);
    // Holds { pincode } parsed from the address when /v1/tickets fails because the pincode isn't registered; null otherwise.
    const [pincodeError, setPincodeError] = useState(null);

    const onSuccess = useCallback(() => {
        closeRaise();
    }, [closeRaise]);

    useEffect(() => {
        const findRma = projectMilestones && projectMilestones.find((mile) => mile.type == MilestoneType.RMA);
        if (findRma) {
            document.getElementById('add_milestone_create_ticket').style.display = 'none';
        } else {
            document.getElementById('add_milestone_create_ticket').style.display = 'block';
        }
    }, [projectMilestones]);


    useEffect(() => {
        if (createdAsset && Object.keys(createdAsset).length > 0) {
            Object.entries(createdAsset).forEach(([key, value]) => {
                methods.setValue(key, value.serial_number, { shouldValidate: true });
            });
            return () => dispatch({ type: CLEAR_CREATED_ASSET_IN_STORE })
        }
    }, [createdAsset]);

    const submit = (data) => {
        lastSubmitDataRef.current = data;
        setPincodeError(null);

        const payload = {
            org_id: actualValueForDropdown(data.organization),
            description: data.description,
            project_id: actualValueForDropdown(data.project),
            source: "PLATFORM",
            priority: "LOW",
            title: data.title || '',
            issue_category: data?.issue_category?.label || data?.issue_category,
            execution_dates: data.execution_dates ? data.execution_dates.map(date => moment(date)) : [],
            external_ticket_id: data.external_ticket_id || '',
        };

        if (actualValueForDropdown(data.enduser) === 'add-new-enduser') {
            payload.create_end_user = data[`enduser:new`]
            delete data.enduser;
            delete data[`enduser:new`];
        } else if (actualValueForDropdown(data.enduser)) {
            payload.end_user_id = actualValueForDropdown(data.enduser)
        }

        if (actualValueForDropdown(data.location_id) === 'add-new-site') {
            const isNewContact = actualValueForDropdown(data[`location_id:site-contact`]) === 'add-new-contact';

            let owners = undefined;
            if (isNewContact) {
                let own = {};
                if (data[`location_id:site-contact:name`]?.trim()) {
                    own.name = data[`location_id:site-contact:name`]
                }
                if (data[`location_id:site-contact:email`]) {
                    own.email = data[`location_id:site-contact:email`]
                }
                if (data[`location_id:site-contact:mobile`]) {
                    own.mobile = data[`location_id:site-contact:mobile`]
                }
                if (Object.keys(own).length > 0) owners = [own];
            }
            if (data[`location_id:site-contact`]?.id) {
                owners = [{
                    id: data[`location_id:site-contact`].id,
                    name: data[`location_id:site-contact`].name,
                    email: data[`location_id:site-contact`].email,
                    mobile: data[`location_id:site-contact`].mobile,
                }]
            };

            const loc = {
                address: transformAddress(data, `location_id:site-address`),
                owners,
                external_id: data[`location_id:site-external-id`],
                type: "SITE",
                region: data[`location_id:region`] ? data[`location_id:region`] : '',
                zone: data[`location_id:zone`] ? data[`location_id:zone`] : ''
            }

            if (actualValueForDropdown(data[`location_id:site-branch-name`]) === 'add-new-branch') {
                loc.branch = data[`location_id:site-branch-name:new`]
            } else if (actualValueForDropdown(data[`location_id:site-branch-name`])) {
                loc.branch_id = actualValueForDropdown(data[`location_id:site-branch-name`])
            }

            payload.location = loc;
        } else {
            payload.location_id = actualValueForDropdown(data.location_id);
        };

        let milestone_id = '';
        const milestones = projectMilestones.map((item, index) => {
            if (index === 0) milestone_id = item.id;
            const elem = { ...item, project_milestone_id: item.id.startsWith('new_') ? undefined : item.id };

            const fieldName = `milestone-${elem.type}-${index}`;
            const filter = Object.fromEntries(Object.entries(data).filter(([key]) => key.startsWith(fieldName)));
            const isLogistics = elem.type === MilestoneType.LOGISTICS;
            const isRMA = elem.type === MilestoneType.RMA;

            if (actualValueForDropdown(filter[`${fieldName}:asset_id`])) {
                elem.asset_id = actualValueForDropdown(filter[`${fieldName}:asset_id`]);
            }

            elem.dispatch_location_id = actualValueForDropdown(filter[`${fieldName}:dispatch_location_id`]) || elem.dispatch_location_id;
            elem.receive_location_id = actualValueForDropdown(filter[`${fieldName}:receive_location_id`]) || elem.receive_location_id;

            if (filter[`${fieldName}:SP_SERVICE`]) {
                elem.payments = {
                    SP_SERVICE: convertIntoPaisa(filter[`${fieldName}:SP_SERVICE`]),
                    PSP_INCENTIVE: convertIntoPaisa(filter[`${fieldName}:PSP_INCENTIVE`]),
                    CLUSTER_INCENTIVE: convertIntoPaisa(filter[`${fieldName}:CLUSTER_INCENTIVE`]),
                    WAREHOUSE_OWNER_INCENTIVE: convertIntoPaisa(filter[`${fieldName}:WAREHOUSE_OWNER_INCENTIVE`]),
                };
            }

            if (elem.id.startsWith('new_')) {
                delete elem.id;
                delete elem.project_milestone_id;
            }

            if (isRMA) {
                elem.location = payload.location;
                elem.location_id = payload.location_id;
            }

            if (isLogistics) {
                if (payload.location && Object.keys(payload.location).length > 0) {
                    elem.dispatch_location = payload.location;
                } else if (payload.location_id) {
                    elem.dispatch_location_id = payload.location_id;
                }

                if (filter[`${fieldName}:is_same_delivery_location`]) {
                    if (payload.location && Object.keys(payload.location).length > 0) {
                        elem.receive_location = payload.location;
                    } else if (payload.location_id) {
                        elem.receive_location_id = payload.location_id;
                    }
                } else {
                    if (filter[`${fieldName}:delivery_location`] && actualValueForDropdown(filter[`${fieldName}:delivery_location`]) === 'add-new-site') {
                        // Changes Start
                        const isNewContact = actualValueForDropdown(filter[`${fieldName}:delivery_location:site-contact`]) === 'add-new-contact';

                        let owners = undefined;
                        if (isNewContact) {
                            let own = {};
                            if (filter[`${fieldName}:delivery_location:site-contact:name`]?.trim()) {
                                own.name = filter[`${fieldName}:delivery_location:site-contact:name`]
                            }
                            if (filter[`${fieldName}:delivery_location:site-contact:email`]) {
                                own.email = filter[`${fieldName}:delivery_location:site-contact:email`]
                            }
                            if (filter[`${fieldName}:delivery_location:site-contact:mobile`]) {
                                own.mobile = filter[`${fieldName}:delivery_location:site-contact:mobile`]
                            }
                            if (Object.keys(own).length > 0) owners = [own];
                        }
                        if (filter[`${fieldName}:delivery_location:site-contact`]?.id) {
                            owners = [{
                                id: filter[`${fieldName}:delivery_location:site-contact`].id,
                                name: filter[`${fieldName}:delivery_location:site-contact`].name,
                                email: filter[`${fieldName}:delivery_location:site-contact`].email,
                                mobile: filter[`${fieldName}:delivery_location:site-contact`].mobile,
                            }]
                        };

                        const loc = {
                            address: transformAddress(data, `${fieldName}:delivery_location:site-address`),
                            owners,
                            external_id: filter[`${fieldName}:delivery_location:site-external-id`],
                            type: "SITE",
                            region: filter[`${fieldName}:delivery_location:region`] ? filter[`${fieldName}:delivery_location:region`] : '',
                            zone: filter[`${fieldName}:delivery_location:zone`] ? filter[`${fieldName}:delivery_location:zone`] : ''
                        }

                        if (actualValueForDropdown(filter[`${fieldName}:delivery_location:site-branch-name`]) === 'add-new-branch') {
                            loc.branch = filter[`${fieldName}:delivery_location:site-branch-name:new`]
                        } else if (actualValueForDropdown(filter[`${fieldName}:delivery_location:site-branch-name`])) {
                            loc.branch_id = actualValueForDropdown(filter[`${fieldName}:delivery_location:site-branch-name`])
                        }

                        elem.receive_location = loc;
                        // Changes Exit
                    } else {
                        elem.receive_location_id = actualValueForDropdown(filter[`${fieldName}:delivery_location`])
                    };
                }
            }

            return elem;
        });

        payload.milestones = milestones;

        if (Array.isArray(data.boq)) {
            payload.bulk_asset_ids = data.boq.map((elem) => {
                return elem.asset_id;
            })
        };

        if (data?.project?.custom_ticket_fields?.length > 0) {
            const custom_fields = [];
            data?.project?.custom_ticket_fields?.forEach((field) => {
                if (data[`custom_ticket_fields:${field.key}`]) {
                    custom_fields.push({
                        label: field.label,
                        value: data[`custom_ticket_fields:${field.key}`],
                        type: field.type
                    });
                }
            });
            if (custom_fields.length > 0) {
                payload.custom_fields = custom_fields;
            }
        };

        if (actualValueForDropdown(data.ticket_contact) === 'add-new-contact') {
            payload.owner = {
                name: data[`ticket_contact:name`] ? data[`ticket_contact:name`] : undefined,
                email: data[`ticket_contact:email`] ? data[`ticket_contact:email`] : undefined,
                mobile: data[`ticket_contact:mobile`] ? data[`ticket_contact:mobile`] : undefined,
            }
        } else if (actualValueForDropdown(data.ticket_contact)) {
            payload.owner_id = actualValueForDropdown(data.ticket_contact);
        }
        const onError = (message) => {
            const isPincodeNotFound = message && /pincode/i.test(message) && /(find|exist|not found)/i.test(message);
            if (isPincodeNotFound) {
                const address = data[`location_id:site-address`] || '';
                const parsedPincode = address ? findPincode(address) : '';
                setPincodeError({ pincode: parsedPincode });
            }
        };

        dispatch(createServiceRequest({ payload, formName: createTicketForm, onSuccess, onError }));
    };

    const handlePincodeAdded = () => {
        setPincodeError(null);
        if (lastSubmitDataRef.current) {
            submit(lastSubmitDataRef.current);
        }
    };

    const openAddPincodeModal = () => {
        dispatch(
            openCustomModal({
                heading: 'Add New Pincode',
                modalWidth: '40rem',
                modalHeight: 'auto',
                modalComponent: (
                    <AddNewPincode
                        pincode={pincodeError?.pincode || ''}
                        onSuccess={() => setPincodeError(null)}
                        onAddAndContinue={handlePincodeAdded}
                        addAndContinueText='Add & Create Ticket'
                    />
                ),
            })
        );
    };

    const label = selectedTicket ? `Need replacement at : ${validateStringValue(selectedTicket?.external_site_id)}` : 'Raise a Ticket';
    const selectedProject = methods.watch('project');
    const milestonesDropdownListData = useSelector((state) => state.project.milestonesDropdown);

    const addNewMilestone = (e) => {
        e.preventDefault();
        setProjectMilestones((prev) => {
            if (!milestonesDropdownListData || !milestonesDropdownListData[0]) {
                return prev;
            }
            return [
                ...prev,
                {
                    ...milestonesDropdownListData[0],
                    id: `new_${Math.floor(Math.random() * 9000000000) + 1000000000}`
                }
            ];
        });
    };

    return (
        <motion.div
            className='fixed bottom-0 right-5 max-w-[80rem] h-[80vh] w-full my-6 mx-auto z-50'
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ duration: 0.3 }}
        >
            <motion.div
                className='rounded-lg shadow-lg relative flex flex-col w-full bg-white h-full '
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.2 }}
            >
                <motion.div
                    className='flex items-center justify-between py-3 px-5 border-b border-scogoddd rounded-t-lg bg-scogoprimary'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 }}
                >
                    <h3 className='text-font12 font-medium text-white'>{label}</h3>
                    <div className="flex items-center gap-3 pr-4">
                        <motion.button
                            id='add_milestone_create_ticket'
                            type="button"
                            className="flex items-center gap-1 px-3 py-2 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors"
                            onClick={(e) => {
                                if (!selectedProject) {
                                    dispatch({ type: SEND_TOAST, payload: { status: 'warning', message: 'Please Select Project' } })
                                    return;
                                }
                                addNewMilestone(e)
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <span className="material-icons-outlined text-font11">add</span>
                            <span>Add Milestone</span>
                        </motion.button>

                        <motion.button
                            type="button"
                            className="flex items-center gap-1 px-3 py-2 bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 transition-colors"
                            onClick={() => {
                                if (!selectedProject) {
                                    dispatch({ type: SEND_TOAST, payload: { status: 'warning', message: 'Please Select Project' } })
                                    return;
                                }
                                setShowBoq(true)
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <span className="material-icons-outlined text-font11">inventory</span>
                            <span>Add BOQ</span>
                        </motion.button>

                        <motion.div
                            className='cursor-pointer'
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={closeRaise}
                        >
                            <img src={`${process.env.PUBLIC_URL}/img/whiteclose.svg`} alt='Close' />
                        </motion.div>
                    </div>
                </motion.div>
                <Form submitRef={submitRef} methods={methods} onSubmit={submit} className='h-11/12'>
                    <motion.div
                        className='relative px-4 flex-auto overflow-y-auto'
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        style={{
                            height: 'calc(100% - 50px)'
                        }}
                    >
                        <CreateTicketForm
                            projectMilestones={projectMilestones}
                            setProjectMilestones={setProjectMilestones}
                            showBoq={showBoq}
                            setShowBoq={setShowBoq}
                        />
                    </motion.div>
                    <motion.div
                        className='flex h-full items-center px-6 border-t border-blueGray-200 rounded-b '
                        style={{
                            height: '50px'
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        <FormButton submitRef={submitRef} />
                        {pincodeError && (
                            <p className='text-scogoclosed text-font09 font-normal ml-4'>
                                This pincode isn't registered in the system.{' '}
                                <span
                                    onClick={openAddPincodeModal}
                                    className='text-scogoprimary font-medium cursor-pointer underline'
                                >
                                    Add this pincode
                                </span>
                            </p>
                        )}
                    </motion.div>
                </Form>
            </motion.div>
        </motion.div>
    );
};

const FormButton = ({ submitRef }) => {
    const { formButtonLoading } = useSelector((state) => state.loading);

    return (
        <motion.div
            className="flex items-center gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
        >

            <ButtonScogoPrimary
                textOrComponent='Create'
                onClick={(e) => {
                    e.preventDefault();
                    submitRef.current.click();
                }}
                loading={formButtonLoading[createTicketForm]}
            />
        </motion.div>
    );
};

export default CreateTicket;

